import { useEffect, useMemo, useState } from "react";
import { ChevronRight, SearchIcon } from "./Icons";
import {
  buildExplorerTree,
  loadCourseCatalog,
  type CourseCatalog,
  type ExplorerNode,
  type OpeningLine,
} from "../lib/openings";

interface OpeningExplorerProps {
  onOpenLine: (pgn: string, name: string) => void;
}

export function OpeningExplorer({ onOpenLine }: OpeningExplorerProps) {
  const [catalog, setCatalog] = useState<CourseCatalog | null>(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadCourseCatalog()
      .then((loaded) => {
        if (active) setCatalog(loaded);
      })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : "Could not load the catalogue.");
      });
    return () => {
      active = false;
    };
  }, []);

  const tree = useMemo(() => (catalog ? buildExplorerTree(catalog) : []), [catalog]);

  const matches = useMemo(() => {
    if (!catalog) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    const scored = catalog.openings
      .map((line) => {
        const name = line.name.toLowerCase();
        const family = line.family.toLowerCase();
        const moves = line.moves.join(" ").toLowerCase();
        if (name.startsWith(needle)) return { line, rank: 0 };
        if (name.includes(needle)) return { line, rank: 1 };
        if (family.includes(needle)) return { line, rank: 2 };
        if (moves.includes(needle)) return { line, rank: 3 };
        if (line.eco.toLowerCase().includes(needle)) return { line, rank: 4 };
        return null;
      })
      .filter((entry): entry is { line: (typeof catalog.openings)[number]; rank: number } => entry !== null);

    return scored
      .sort((a, b) => a.rank - b.rank || a.line.name.localeCompare(b.line.name))
      .map((entry) => entry.line);
  }, [catalog, query]);

  const toggle = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (error) {
    return (
      <div className="explorer">
        <p className="planned__desc">{error}</p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="explorer">
        <div className="explorer__skeleton">
          <div className="skeleton skeleton--wide" />
          <div className="skeleton" />
          <div className="skeleton skeleton--wide" />
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="explorer">
      <div className="explorer__head">
        <div>
          <h2 className="explorer__title">Opening explorer</h2>
          <p className="explorer__sub">
            {catalog.openings.length.toLocaleString()} lines from the Lichess opening collection,
            released under CC0. Open one to analyse it in the board.
          </p>
        </div>
        <label className="field field--search">
          <SearchIcon />
          <input
            type="search"
            value={query}
            placeholder="Search a name, ECO or move"
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search openings"
          />
        </label>
      </div>

      {query.trim() ? (
        <ul className="explorer__results">
          {matches.slice(0, 80).map((line) => (
            <li key={line.slug}>
              <button className="explorer__result" onClick={() => openLine(line, onOpenLine)}>
                <span className="explorer__result-name">{line.name}</span>
                <span className="explorer__result-meta">
                  {line.eco} · {line.family}
                </span>
                <span className="explorer__result-moves">{line.moves.join(" ")}</span>
              </button>
            </li>
          ))}
          {!matches.length ? <li className="explorer__empty">Nothing matched that search.</li> : null}
        </ul>
      ) : (
        <div className="explorer__tree">
          {tree.map((node) => (
            <ExplorerBranch
              key={node.key}
              node={node}
              expanded={expanded}
              onToggle={toggle}
              onOpenLine={onOpenLine}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ExplorerBranchProps {
  node: ExplorerNode;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onOpenLine: (pgn: string, name: string) => void;
}

function ExplorerBranch({ node, expanded, onToggle, onOpenLine }: ExplorerBranchProps) {
  const isOpen = expanded.has(node.key);
  const line = node.lines[0];

  return (
    <div className="explorer__branch">
      <div className="explorer__node">
        <button
          className="explorer__move"
          onClick={() => onToggle(node.key)}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? "Hide" : "Show"} continuations after ${node.san}`}
        >
          <ChevronRight className={isOpen ? "explorer__chevron explorer__chevron--open" : "explorer__chevron"} />
          <span className="explorer__move-number">
            {node.color === "white" ? `${node.moveNumber}.` : `${node.moveNumber}...`}
          </span>
          <span className="explorer__move-san">{node.san}</span>
        </button>
        {line ? (
          <button
            className="explorer__line"
            onClick={() => openLine(line, onOpenLine)}
            title={`Analyse ${line.name}`}
          >
            {line.name}
            <span className="explorer__eco">{line.eco}</span>
          </button>
        ) : null}
      </div>
      {isOpen && node.children.length ? (
        <div className="explorer__children">
          {node.children.map((child) => (
            <ExplorerBranch
              key={child.key}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              onOpenLine={onOpenLine}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function openLine(line: OpeningLine, onOpenLine: (pgn: string, name: string) => void) {
  const moves = line.moves
    .map((san, index) => (index % 2 === 0 ? `${Math.floor(index / 2) + 1}. ${san}` : san))
    .join(" ");
  const pgn = `[Event "${line.name}"]\n[ECO "${line.eco}"]\n[Opening "${line.name}"]\n\n${moves} *`;
  onOpenLine(pgn, line.name);
}
